import { Controller } from "@hotwired/stimulus"
import { SectionCourseLoader } from "./page_editor_modules/section_course_loader.js"
import { TextEditor } from "./page_editor_modules/text_editor.js"
import { ImageChangeHandler } from "./page_editor_modules/image_change_handler.js"
import { SaveButton } from "./page_editor_modules/save_button.js"
import { NavItemHandler } from "./page_editor_modules/nav_item_handler.js"
import { SectionControls } from "./page_editor_modules/section_controls.js"
import { ImageLinkHandler } from "./page_editor_modules/image_link_handler.js"
// import "/app/assets/stylesheets/new_styles/page_editor.scss"

export default class extends Controller {
  static targets = ["saveButton"];

  connect() {
    // Initialize modules
    this.sectionCourseLoader = new SectionCourseLoader(this);
    this.textEditor = new TextEditor(this);
    this.imageChangeHandler = new ImageChangeHandler(this);
    this.saveButton = new SaveButton(this);
    this.navItemHandler = new NavItemHandler(this);
    this.sectionControls = new SectionControls(this);
    this.imageLinkHandler = new ImageLinkHandler(this);

    // Initialize functionality
    // this.sectionCourseLoader.loadSectionCourse();
    this.saveNavbarState();
    this.addNavbarBeforeFirstSection();
    this.textEditor.initializeTextEditor();
    this.imageChangeHandler.initializeImageChangeHandlers();
    this.saveButton.initializeSaveButton();
    this.navItemHandler.initializeNavItemHandlers();
    this.sectionControls.initializeSectionControls();
    this.imageLinkHandler.initializeImageLinkHandler();
  }

  disconnect() {
    console.log('Disconnecting PageEditorController...');

    // Restore navbar state before other cleanup


    // Store references before cleanup
    const elementsWithListeners = [];

    // Find all elements that might have event listeners
    const allElements = this.element.querySelectorAll('*');
    allElements.forEach(el => {
      // Clone and replace elements to remove all event listeners
      const newElement = el.cloneNode(true);
      if (el.parentNode) {
        elementsWithListeners.push({ original: el, replacement: newElement });
      }
    });

    // Array of all modules for easier cleanup
    const modules = [
      this.sectionCourseLoader,
      this.textEditor,
      this.imageChangeHandler,
      this.saveButton,
      this.navItemHandler,
      this.sectionControls,
      this.imageLinkHandler
    ];

    // Clean up all module event handlers
    modules.forEach(module => {
      if (module) {
        if (typeof module.cleanup === 'function') {
          try {
            module.cleanup();
          } catch (error) {
            console.warn('Error during module cleanup:', error);
          }
        }
        // Also try to remove any stored event listeners if they exist
        if (module.eventListeners && Array.isArray(module.eventListeners)) {
          module.eventListeners.forEach(({ element, event, handler }) => {
            if (element && element.removeEventListener) {
              element.removeEventListener(event, handler);
            }
          });
        }
      }
    });

    // Replace elements to remove all event listeners (aggressive cleanup)
    elementsWithListeners.forEach(({ original, replacement }) => {
      if (original.parentNode) {
        original.parentNode.replaceChild(replacement, original);
      }
    });

    // Clear any intervals or timeouts that might be running
    for (let i = 1; i < 99999; i++) {
      window.clearInterval(i);
      window.clearTimeout(i);
    }

    // Remove global event listeners on document and window
    const commonEvents = ['click', 'change', 'input', 'keydown', 'keyup', 'mousedown', 'mouseup', 'mouseover', 'mouseout'];
    commonEvents.forEach(eventType => {
      document.removeEventListener(eventType, this.handleGlobalEvent, true);
      window.removeEventListener(eventType, this.handleGlobalEvent, true);
    });

    // Clear module references
    this.sectionCourseLoader = null;
    this.textEditor = null;
    this.imageChangeHandler = null;
    this.saveButton = null;
    this.navItemHandler = null;
    this.sectionControls = null;
    this.imageLinkHandler = null;
  }

  // Dummy handler for cleanup
  handleGlobalEvent(event) {
    // This is just for cleanup purposes
  }

  showToaster(message, duration = 3000) {
    let toasterContainer = document.getElementById('toaster-container');
    if (!toasterContainer) {
      toasterContainer = document.createElement('div');
      toasterContainer.id = 'toaster-container';
      toasterContainer.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 10px;
      `;
      document.body.appendChild(toasterContainer);
    }

    const toaster = document.createElement('div');
    toaster.textContent = message;
    toaster.style.cssText = `
      background-color: #28a745;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      min-width: 250px;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      font-size: 14px;
    `;

    toasterContainer.appendChild(toaster);

    // Animate in
    setTimeout(() => {
      toaster.style.transform = 'translateX(0)';
    }, 10);

    // Animate out and remove
    setTimeout(() => {
      toaster.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (toaster.parentNode) {
          toaster.parentNode.removeChild(toaster);
        }
      }, 300);
    }, duration);
  }

  // Helper function to convert RGB to HEX
  rgbToHex(rgb) {
    // Convert rgb color to hex
    if (rgb.startsWith('#')) {
      return rgb;
    }

    const values = rgb.match(/\d+/g);
    if (!values || values.length < 3) {
      return '#000000';
    }

    const r = parseInt(values[0]);
    const g = parseInt(values[1]);
    const b = parseInt(values[2]);

    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  saveNavbarState() {
    const existingNavbar = document.querySelector('nav.navbar, .navbar');
    const header = document.querySelector('header');

    if (existingNavbar) {
      this.navbarState = {
        parentElement: existingNavbar.parentElement,
        nextSibling: existingNavbar.nextSibling,
        classList: Array.from(existingNavbar.classList),
        styles: {
          position: existingNavbar.style.position,
          top: existingNavbar.style.top,
          left: existingNavbar.style.left,
          width: existingNavbar.style.width,
          zIndex: existingNavbar.style.zIndex
        }
      };
    }

    if (header) {
      this.headerState = {
        position: header.style.position
      };
    }
  }

  restoreNavbarState() {
    const existingNavbar = document.querySelector('nav.navbar, .navbar');
    const header = document.querySelector('header');

    if (existingNavbar && this.navbarState) {
      // Restore classes
      existingNavbar.className = '';
      this.navbarState.classList.forEach(cls => {
        existingNavbar.classList.add(cls);
      });

      // Restore styles
      Object.keys(this.navbarState.styles).forEach(style => {
        existingNavbar.style[style] = this.navbarState.styles[style];
      });

      // Restore DOM position
      if (this.navbarState.parentElement) {
        this.navbarState.parentElement.insertBefore(existingNavbar, this.navbarState.nextSibling);
      }
    }

    if (header && this.headerState) {
      // Restore header styles
      header.style.position = this.headerState.position;
    }

    // Clear saved states
    this.navbarState = null;
    this.headerState = null;
  }

  addNavbarBeforeFirstSection() {
    const existingNavbar = document.querySelector('nav.navbar, .navbar');
    const firstSection = document.querySelector('section:first-of-type, .section:first-of-type, main section:first-child');

    if (existingNavbar && firstSection) {
      // Remove any existing positioning classes
      existingNavbar.classList.remove('fixed-top', 'fixed-bottom', 'sticky-top');

      // Add Bootstrap classes for positioning at left top corner with full width
      // existingNavbar.classList.add('navbar-expand-lg', 'position-absolute', 'top-0', 'start-0', 'w-100');

      // Clear inline styles and set full width
      existingNavbar.style.position = '';
      existingNavbar.style.top = '';
      existingNavbar.style.left = '';
      existingNavbar.style.width = '100%';
      existingNavbar.style.zIndex = '';

      // Ensure first section has relative positioning for absolute positioning to work
      firstSection.style.position = 'relative';

      // Move navbar before first section
      firstSection.parentNode.insertBefore(existingNavbar, firstSection);
    }
  }
}


